import type { AppConfig } from "../../composables/ConfigStore/useConfigStore";
import type { ConfigField, DebugApp, DebugAppConfig } from "./types";

export type ConfigRecord = Record<string, AppConfig>;
export type EnvironmentKey = keyof ConfigRecord;

/** The addresses of the login app. `authSidebarUrl` follows the preset but is not in the form. */
export interface AuthAppConfig {
  loginUrl: string
  apiUrl: string
  webUrl: string
  authSidebarUrl?: string
  presetName?: string | null
}

export type AuthPresetKey = "localhost" | "frontend_localhost" | "backend_localhost" | "sherin" | "dev" | "staging" | "production";
export type AuthConfigRecord = Record<AuthPresetKey, AuthAppConfig>;

export const config = <{
  cms: ConfigRecord
  web: ConfigRecord
  integrations: ConfigRecord
  auth: AuthConfigRecord
}>{
  cms: {
    localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost",
      cdnUrl: "https://usercontent.verbleif.localhost",
      webUrl: "http://app.verbleif.localhost:5173",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "http://cms.verbleif.localhost:5174",
      loginClientId: "a1b2c3d4e5f6789012345678abcdef01",
      authSidebarUrl: "https://verbleif.localhost",
    },
    frontend_localhost: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      cdnUrl: "https://usercontent.verbleif.dev",
      webUrl: "http://app.verbleif.localhost:5173",
      appEnv: "local",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "http://cms.verbleif.localhost:5174",
      loginClientId: "47aa41cbdde7b579b0acc306d54bc058",
      authSidebarUrl: "https://verbleif.dev",
    },
    backend_localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost",
      cdnUrl: "https://usercontent.verbleif.localhost",
      webUrl: "https://app.verbleif.dev",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "https://cms.verbleif.dev",
      loginClientId: "a1b2c3d4e5f6789012345678abcdef01",
      authSidebarUrl: "https://verbleif.localhost",
    },
    sherin: {
      apiUrl: "https://api.sherin.vitrion.dev",
      mercureUrl: "https://mercure.sherin.vitrion.dev",
      loginUrl: "https://login.sherin.vitrion.dev",
      loginRedirect: "http://cms.verbleif.localhost:5174",
      cdnUrl: "https://cdn.sherin.vitrion.dev",
      appEnv: "dev",
      loginClientId: "a1b2c3d4e5f6789012345678abcdef01",
      authSidebarUrl: "https://verbleif.dev",
    },
    testing: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      cdnUrl: "https://cdn.verbleif.dev",
      webUrl: "https://app.verbleif.dev",
      appEnv: "dev",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "http://cms.verbleif.dev",
      loginClientId: "47aa41cbdde7b579b0acc306d54bc058",
      authSidebarUrl: "https://verbleif.dev",
    },
    staging: {
      apiUrl: "https://api.staging.verbleif.com",
      mercureUrl: "https://mercure.staging.verbleif.com",
      cdnUrl: "https://cdn.staging.verbleif.com",
      webUrl: "https://app.staging.verbleif.com",
      appEnv: "staging",
      loginUrl: "https://login.staging.verbleif.com",
      loginRedirect: "https://cms.staging.verbleif.com",
      loginClientId: "61d3fa9919db6eb9cc241d49a36a393e",
      authSidebarUrl: "https://staging.verbleif.com",
    },
    production: {
      apiUrl: "https://api.verbleif.com",
      mercureUrl: "https://mercure.verbleif.com",
      cdnUrl: "https://cdn.verbleif.com",
      webUrl: "https://app.verbleif.com",
      appEnv: "production",
      loginUrl: "https://login.verbleif.com",
      loginRedirect: "https://cms.verbleif.com",
      loginClientId: "61d3fa9919db6eb9cc241d49a36a393e",
      authSidebarUrl: "https://verbleif.com",
    },
  },
  web: {
    localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost",
      cdnUrl: "https://usercontent.verbleif.localhost",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "http://app.verbleif.localhost:5173",
      loginClientId: "f3bee0a0f13225abc6216d29ffce28a5",
      authSidebarUrl: "https://verbleif.localhost",
      developerUrl: "https://developer.verbleif.localhost",
    },
    frontend_localhost: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      cdnUrl: "https://usercontent.verbleif.dev",
      appEnv: "local",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "http://app.verbleif.localhost:5173",
      loginClientId: "11b3f1633604dda00c6ccc99b00b08f8",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    backend_localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost",
      cdnUrl: "https://usercontent.verbleif.localhost",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "https://app.verbleif.dev",
      loginClientId: "f3bee0a0f13225abc6216d29ffce28a5",
      authSidebarUrl: "https://verbleif.localhost",
      developerUrl: "https://developer.verbleif.localhost",
    },
    sherin: {
      apiUrl: "https://api.sherin.vitrion.dev",
      mercureUrl: "https://mercure.sherin.vitrion.dev",
      loginUrl: "https://login.sherin.vitrion.dev",
      loginRedirect: "http://app.verbleif.localhost:5173",
      cdnUrl: "https://cdn.sherin.vitrion.dev",
      appEnv: "dev",
      loginClientId: "f3bee0a0f13225abc6216d29ffce28a5",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    testing: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "https://app.verbleif.dev",
      cdnUrl: "https://cdn.verbleif.dev",
      appEnv: "dev",
      loginClientId: "11b3f1633604dda00c6ccc99b00b08f8",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    staging: {
      apiUrl: "https://api.staging.verbleif.com",
      mercureUrl: "https://mercure.staging.verbleif.com",
      loginUrl: "https://login.staging.verbleif.com",
      loginRedirect: "https://app.staging.verbleif.com",
      loginClientId: "80847465624efa7d6a98d0023d70e53b",
      cdnUrl: "https://cdn.staging.verbleif.com",
      appEnv: "staging",
      authSidebarUrl: "https://staging.verbleif.com",
      developerUrl: "https://developer.staging.verbleif.com",
    },
    production: {
      apiUrl: "https://api.verbleif.com",
      mercureUrl: "https://mercure.verbleif.com",
      loginUrl: "https://login.verbleif.com",
      loginRedirect: "https://app.verbleif.com",
      loginClientId: "80847465624efa7d6a98d0023d70e53b",
      cdnUrl: "https://cdn.verbleif.com",
      appEnv: "production",
      authSidebarUrl: "https://verbleif.com",
      developerUrl: "https://developer.verbleif.com",
    },
  },
  integrations: {
    localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost",
      cdnUrl: "https://cdn.verbleif.localhost",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "http://integrations.verbleif.localhost:5176",
      loginClientId: "59d77a7716b5c89356b1fb9640d61327",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.localhost",
      developerUrl: "https://developer.verbleif.localhost",
    },
    frontend_localhost: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      cdnUrl: "https://cdn.verbleif.dev",
      appEnv: "local",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "http://integrations.verbleif.localhost:5176",
      loginClientId: "59d77a7716b5c89356b1fb9640d61327",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    backend_localhost: {
      apiUrl: "https://api.verbleif.localhost",
      mercureUrl: "https://mercure.verbleif.localhost:81",
      cdnUrl: "https://cdn.verbleif.localhost",
      appEnv: "local",
      loginUrl: "https://login.verbleif.localhost",
      loginRedirect: "https://integrations.verbleif.dev",
      loginClientId: "59d77a7716b5c89356b1fb9640d61327",
      webUrl: "https://app.verbleif.dev",
      authSidebarUrl: "https://verbleif.localhost",
      developerUrl: "https://developer.verbleif.localhost",
    },
    sherin: {
      apiUrl: "https://api.sherin.vitrion.dev",
      mercureUrl: "https://mercure.sherin.vitrion.dev",
      cdnUrl: "https://cdn.sherin.vitrion.dev",
      appEnv: "dev",
      loginUrl: "https://login.sherin.vitrion.dev",
      loginRedirect: "http://integrations.sherin.vitrion.dev",
      loginClientId: "59d77a7716b5c89356b1fb9640d61327",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    testing: {
      apiUrl: "https://api.verbleif.dev",
      mercureUrl: "https://mercure.verbleif.dev",
      cdnUrl: "https://cdn.verbleif.dev",
      appEnv: "dev",
      loginUrl: "https://login.verbleif.dev",
      loginRedirect: "https://integrations.verbleif.dev",
      loginClientId: "59d77a7716b5c89356b1fb9640d61327",
      webUrl: "https://app.verbleif.dev",
      authSidebarUrl: "https://verbleif.dev",
      developerUrl: "https://developer.verbleif.dev",
    },
    staging: {
      apiUrl: "https://api.staging.verbleif.com",
      mercureUrl: "https://mercure.staging.verbleif.com",
      cdnUrl: "https://cdn.staging.verbleif.com",
      appEnv: "staging",
      loginUrl: "https://login.staging.verbleif.com",
      loginRedirect: "https://integrations.staging.verbleif.com",
      loginClientId: "0949cdcc7615e9be2beedb650b73582c",
      webUrl: "https://app.staging.verbleif.com",
      authSidebarUrl: "https://staging.verbleif.com",
      developerUrl: "https://developer.staging.verbleif.com",
    },
    production: {
      apiUrl: "https://api.verbleif.com",
      mercureUrl: "https://mercure.verbleif.com",
      cdnUrl: "https://cdn.verbleif.com",
      appEnv: "production",
      loginUrl: "https://login.verbleif.com",
      loginRedirect: "https://integrations.verbleif.com",
      loginClientId: "0949cdcc7615e9be2beedb650b73582c",
      webUrl: "https://app.verbleif.com",
      authSidebarUrl: "https://verbleif.com",
      developerUrl: "https://developer.verbleif.com",
    },
  },
  auth: {
    localhost: {
      loginUrl: "https://login.verbleif.localhost",
      apiUrl: "https://api.verbleif.localhost",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.localhost",
    },
    frontend_localhost: {
      loginUrl: "https://login.verbleif.dev",
      apiUrl: "https://api.verbleif.dev",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.dev",
    },
    backend_localhost: {
      loginUrl: "https://login.verbleif.localhost",
      apiUrl: "https://api.verbleif.localhost",
      webUrl: "https://app.verbleif.dev",
      authSidebarUrl: "https://verbleif.localhost",
    },
    sherin: {
      loginUrl: "https://login.sherin.vitrion.dev",
      apiUrl: "https://api.sherin.vitrion.dev",
      webUrl: "http://app.verbleif.localhost:5173",
      authSidebarUrl: "https://verbleif.dev",
    },
    dev: {
      loginUrl: "https://login.verbleif.dev",
      apiUrl: "https://api.verbleif.dev",
      webUrl: "https://app.verbleif.dev",
      authSidebarUrl: "https://verbleif.dev",
    },
    staging: {
      loginUrl: "https://login.staging.verbleif.com",
      apiUrl: "https://api.staging.verbleif.com",
      webUrl: "https://app.staging.verbleif.com",
      authSidebarUrl: "https://staging.verbleif.com",
    },
    production: {
      loginUrl: "https://login.verbleif.com",
      apiUrl: "https://api.verbleif.com",
      webUrl: "https://app.verbleif.com",
      authSidebarUrl: "https://verbleif.com",
    },
  },
};

/** Colour of an environment in the tiles and the header label. */
export type PresetTone = "primary" | "secondary" | "warning" | "danger" | "info" | "gray";

export interface PresetMeta {
  /** Translation key of the tile title, or `name` for a proper name that is not translated. */
  label?: string
  name?: string
  /** Translation key of the tile subtitle, or `host` for a host name that is not translated. */
  subtitle?: string
  host?: string
  tone: PresetTone
  /** Switching to it asks for confirmation first. */
  confirm?: boolean
}

/** Every preset key of every app, in tile order. `testing` (Web, CMS, Integrations) and `dev` (auth) are both Dev. */
const PRESET_META: Record<string, PresetMeta> = {
  localhost: { label: "debug_tools.environment.presets.localhost", subtitle: "debug_tools.environment.presets.localhost_subtitle", tone: "primary" },
  frontend_localhost: { label: "debug_tools.environment.presets.frontend_localhost", subtitle: "debug_tools.environment.presets.frontend_localhost_subtitle", tone: "primary" },
  backend_localhost: { label: "debug_tools.environment.presets.backend_localhost", subtitle: "debug_tools.environment.presets.backend_localhost_subtitle", tone: "primary" },
  sherin: { name: "Sherin", host: "sherin.vitrion.dev", tone: "primary" },
  testing: { label: "debug_tools.environment.presets.dev", host: "verbleif.dev", tone: "secondary" },
  dev: { label: "debug_tools.environment.presets.dev", host: "verbleif.dev", tone: "secondary" },
  staging: { label: "debug_tools.environment.presets.staging", host: "staging.verbleif.com", tone: "warning" },
  production: { label: "debug_tools.environment.presets.production", host: "verbleif.com", tone: "danger", confirm: true },
};

export function presetMeta(key: string): PresetMeta {
  const meta = PRESET_META[key];
  if (!meta) {
    throw new Error(`Unknown debug preset: ${key}`);
  }
  return meta;
}

export function presetsFor(app: DebugApp): Record<string, DebugAppConfig> {
  return config[app];
}

/** Preset keys of an app in tile order. */
export function presetKeysFor(app: DebugApp): string[] {
  const keys = Object.keys(presetsFor(app));
  const order = Object.keys(PRESET_META);
  return [...keys].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function field(key: string, slug: string, type: ConfigField["type"], group?: ConfigField["group"]): ConfigField {
  return {
    key,
    label: `debug_tools.environment.fields.${slug}.label`,
    placeholder: `debug_tools.environment.fields.${slug}.placeholder`,
    type,
    required: true,
    group,
  };
}

const API_URL = field("apiUrl", "api_url", "url", "connections");
const MERCURE_URL = field("mercureUrl", "mercure_url", "url", "connections");
const CDN_URL = field("cdnUrl", "cdn_url", "url", "connections");
const WEB_URL = field("webUrl", "web_url", "url", "connections");
const APP_ENV = field("appEnv", "app_env", "env", "connections");
const LOGIN_URL = field("loginUrl", "login_url", "url", "login");
const LOGIN_CLIENT_ID = field("loginClientId", "login_client_id", "text", "login");
const LOGIN_REDIRECT = field("loginRedirect", "login_redirect", "url", "login");

/**
 * The addresses the Omgeving form edits per app. `authSidebarUrl` and `developerUrl` stay out of it: they follow the
 * preset. The login app has no groups, only its three addresses.
 */
export const CONFIG_FIELDS: Record<DebugApp, ConfigField[]> = {
  web: [API_URL, MERCURE_URL, CDN_URL, APP_ENV, LOGIN_URL, LOGIN_CLIENT_ID, LOGIN_REDIRECT],
  cms: [API_URL, MERCURE_URL, CDN_URL, WEB_URL, APP_ENV, LOGIN_URL, LOGIN_CLIENT_ID, LOGIN_REDIRECT],
  integrations: [API_URL, MERCURE_URL, CDN_URL, WEB_URL, APP_ENV, LOGIN_URL, LOGIN_CLIENT_ID, LOGIN_REDIRECT],
  auth: [
    { ...LOGIN_URL, group: undefined },
    { ...API_URL, group: undefined },
    { ...WEB_URL, group: undefined },
  ],
};

/** The values of the App-omgeving select. */
export const APP_ENVIRONMENTS = ["local", "dev", "staging", "production"] as const;
